<script lang="ts">
  // Hearth drawing controller. Owns the drawing state and bridges the
  // pure state module to the MapLibre map via the layer helpers in
  // `draw/mapLayers.ts`. Renders the toolbar and reacts to map clicks.
  //
  // S-04-04 implements snap-to-road. S-04-05 fills in the free-draw
  // branch — the controller is already mode-aware so the second story is
  // a small delta.
  //
  // Hard rules:
  // - No state in localStorage/sessionStorage. Reload = clean slate.
  // - User-facing copy in Swedish; logs in English (none here yet).
  // - GraphHopper key never lives in the client bundle — fetchRoute hits
  //   the backend proxy at `/api/admin/route`.

  import type { Map as MapLibreMap, MapMouseEvent } from 'maplibre-gl';
  import DrawingToolbar from './DrawingToolbar.svelte';
  import {
    appendFreeDragSample,
    appendFreeVertex,
    appendSnapWaypoint,
    cancelDrawing,
    commitFreeStrokeIfAny,
    createInitialDrawingState,
    setMode,
    undoLast,
    type DrawingState,
  } from './draw/drawingState';
  import type { Coordinate, DrawingMode } from './draw/types';
  import {
    DRAW_LAYER_IDS,
    DRAW_SOURCE_IDS,
    ensureDrawingLayers,
    removeDrawingLayers,
    syncDrawingFeatures,
  } from './draw/mapLayers';

  interface Props {
    map: MapLibreMap | null;
    /**
     * Optional callback that fires whenever the internal drawing state
     * changes. Lets a parent vy (t.ex. HearthsView i S-04-13) läsa
     * polylines via `buildPolylines(state)` när admin sparar härden.
     * Komponenten äger fortfarande state — callback är read-only ut.
     */
    // eslint-disable-next-line no-unused-vars
    onStateChange?: (state: DrawingState) => void;
  }

  const { map, onStateChange }: Props = $props();

  let drawing: DrawingState = $state(createInitialDrawingState());

  // Notify parent when drawing state changes. Vi använder $effect här så
  // att alla mutations (klick, drag, undo, mode-byte) synkas — inte bara
  // de som går via en specifik handler.
  $effect(() => {
    onStateChange?.(drawing);
  });
  let isBusy: boolean = $state(false);
  let errorMessage: string | null = $state(null);

  const canUndo: boolean = $derived(drawing.waypoints.length > 0 || drawing.segments.length > 0);
  // S-04-26: snap-mode-status nämner nu uttryckligen att snap till väg
  // sker vid spara — adresserar Jonas-feedback "vet inte om snappen
  // funkade" från live-test 2026-05-05. Final-snap-modellen från
  // S-04-05b kör en enda GraphHopper-call vid spara, inte per klick.
  const statusMessage: string | null = $derived(
    drawing.mode === 'snap-to-road'
      ? drawing.waypoints.length === 0
        ? 'Klicka i kartan för att lägga första punkten. Snap till väg sker vid spara.'
        : `Punkt ${drawing.waypoints.length} satt. Klicka för nästa punkt — snap till väg sker vid spara.`
      : drawing.waypoints.length === 0
        ? 'Klicka eller dra i kartan för att börja rita fritt.'
        : `${drawing.waypoints.length} punkter ritade. Klicka eller dra för att fortsätta.`,
  );

  /**
   * Wire map click → drawing-state action. Snap-to-road clicks call the
   * route service and merge the returned polyline; free clicks are pushed
   * directly. Errors surface in the toolbar status line.
   */
  async function handleMapClick(event: MapMouseEvent): Promise<void> {
    if (!map) return;
    const click: Coordinate = {
      latitude: event.lngLat.lat,
      longitude: event.lngLat.lng,
    };

    errorMessage = null;

    if (drawing.mode === 'free') {
      drawing = appendFreeVertex(drawing, click);
      return;
    }

    // snap-to-road: first click only records, no route call needed.
    if (drawing.waypoints.length === 0) {
      drawing = appendSnapWaypoint(drawing, click, null);
      return;
    }

    // S-04-05b final-snap-modellen (2026-05-05): vi anropar INTE
    // GraphHopper per klick längre. Pilot-incident visade att free-
    // tier-kvoten bröts efter ~10 klick. Den faktiska snap:en sker
    // EN gång vid spara via HearthsView (som skickar alla waypoints
    // i en enda GraphHopper-call). Under aktiv ritning visar vi rak
    // linje mellan klick som omedelbar visuell feedback.
    const previous = drawing.waypoints[drawing.waypoints.length - 1];
    drawing = appendSnapWaypoint(drawing, click, [previous, click]);
  }

  function handleModeChange(next: DrawingMode): void {
    drawing = setMode(drawing, next);
    errorMessage = null;
  }

  function handleUndo(): void {
    drawing = undoLast(drawing);
    errorMessage = null;
  }

  function handleCancel(): void {
    drawing = cancelDrawing(drawing);
    errorMessage = null;
  }

  // Drag-to-draw bookkeeping for S-04-05 free mode. We don't keep this
  // in `$state` because it's a transient per-stroke flag; reactivity
  // would be wasted churn on every mousemove sample.
  let isDragging = false;
  // Throttle mousemove samples — 30 ms is roughly 33 Hz, plenty smooth
  // for visual feedback without letting waypoints accumulate to the
  // tens of thousands on a long stroke.
  const DRAG_SAMPLE_INTERVAL_MS = 30;
  let lastSampleAt = 0;

  // Attach click listener and source/layers when a map is available.
  // The cleanup function is the key bit: when the map instance changes
  // (e.g. style switch) we tear down listeners and layers cleanly.
  $effect(() => {
    if (!map) return;
    ensureDrawingLayers(map);
    const clickHandler = (e: MapMouseEvent): void => {
      // Suppress click handling during a drag stroke — MapLibre fires a
      // click on mouseup at the end of a drag and we'd double-record
      // the final vertex otherwise.
      if (isDragging) return;
      void handleMapClick(e);
    };

    // Free-mode drag = continuous draw (S-04-05 story spec).
    // mousedown: enter drag mode, disable map pan, push first vertex.
    // mousemove: throttle then push samples.
    // mouseup: commit the stroke, re-enable pan.
    const mouseDownHandler = (e: MapMouseEvent): void => {
      if (drawing.mode !== 'free') return;
      isDragging = true;
      // Disable the map's drag-to-pan while drawing so the gesture is
      // unambiguously "draw a stroke". Re-enabled on mouseup.
      map.dragPan.disable();
      lastSampleAt = performance.now();
      drawing = appendFreeDragSample(drawing, {
        latitude: e.lngLat.lat,
        longitude: e.lngLat.lng,
      });
      errorMessage = null;
    };
    const mouseMoveHandler = (e: MapMouseEvent): void => {
      if (!isDragging) return;
      const now = performance.now();
      if (now - lastSampleAt < DRAG_SAMPLE_INTERVAL_MS) return;
      lastSampleAt = now;
      drawing = appendFreeDragSample(drawing, {
        latitude: e.lngLat.lat,
        longitude: e.lngLat.lng,
      });
    };
    const mouseUpHandler = (): void => {
      if (!isDragging) return;
      isDragging = false;
      map.dragPan.enable();
      // Commit the stroke so a subsequent click/drag starts a new one
      // and `setMode` doesn't have to do that work.
      drawing = commitFreeStrokeIfAny(drawing);
    };

    map.on('click', clickHandler);
    map.on('mousedown', mouseDownHandler);
    map.on('mousemove', mouseMoveHandler);
    map.on('mouseup', mouseUpHandler);

    return () => {
      map.off('click', clickHandler);
      map.off('mousedown', mouseDownHandler);
      map.off('mousemove', mouseMoveHandler);
      map.off('mouseup', mouseUpHandler);
      // Defensive: if the cleanup runs mid-drag, restore pan so the
      // map isn't permanently locked.
      try {
        map.dragPan.enable();
      } catch {
        /* map already destroyed */
      }
      removeDrawingLayers(map);
    };
  });

  // Push state updates to the map source whenever drawing state changes.
  $effect(() => {
    if (!map) return;
    syncDrawingFeatures(map, drawing);
  });

  // Expose the layer/source identifiers for tests and integration
  // points; useful for component tests of the parent view.
  export const layerIds = DRAW_LAYER_IDS;
  export const sourceIds = DRAW_SOURCE_IDS;
</script>

<div class="hearth-drawing">
  <DrawingToolbar
    mode={drawing.mode}
    {canUndo}
    {isBusy}
    {statusMessage}
    {errorMessage}
    onModeChange={handleModeChange}
    onUndo={handleUndo}
    onCancel={handleCancel}
  />
</div>

<style>
  .hearth-drawing {
    pointer-events: auto;
  }
</style>
